import { FunctionComponent } from "react";
import { DecisionAlert } from "../../models/DecisionSupport";
import { OverviewScope, VALUE_LABELS, ValueCode } from "../../models/OverviewDashboard";
import {
    HIGH_SCORE_THRESHOLD,
    LOW_SCORE_THRESHOLD
} from "../../services/DecisionSupportService";
import styles from "./DecisionAlertsPanel.module.css";

interface DecisionAlertsPanelProps {
    alerts: DecisionAlert[];
    alertScope: OverviewScope;
    poleCount: number;
    servicesCount: number;
    onScopeChange: (scope: OverviewScope) => void;
    onAction?: (alert: DecisionAlert) => void;
}

const VALUE_CLASS: Record<ValueCode, string> = {
    bienveillance: styles.bienveillance,
    engagement: styles.engagement,
    "faire-equipe": styles.faireEquipe,
    respect: styles.respect
};

const DecisionAlertsPanel: FunctionComponent<DecisionAlertsPanelProps> = ({
    alerts,
    alertScope,
    poleCount,
    servicesCount,
    onScopeChange,
    onAction
}) => (
    <section className={styles.panel}>
        <header className={styles.header}>
            <div>
                <h3>Alertes actives</h3>
                <p>Suggestions calculées à partir de l'ensemble des valeurs disponibles.</p>
            </div>

            <div className={styles.scopeToggle} role="group" aria-label="Périmètre des alertes">
                <button
                    type="button"
                    className={alertScope === "pole" ? styles.activeScope : undefined}
                    onClick={() => onScopeChange("pole")}
                >
                    Pôle <span>{poleCount}</span>
                </button>
                <button
                    type="button"
                    className={alertScope === "services" ? styles.activeScope : undefined}
                    onClick={() => onScopeChange("services")}
                >
                    Services <span>{servicesCount}</span>
                </button>
            </div>
        </header>

        {alerts.length === 0 ? (
            <div className={styles.empty}>
                Aucune alerte active sur ce périmètre pour le moment.
            </div>
        ) : (
            <div className={styles.list}>
                {alerts.map((alert) => {
                    const { suggestion } = alert;
                    const isLow = suggestion.kind === "request-workshop-or-training";
                    const threshold = isLow ? LOW_SCORE_THRESHOLD : HIGH_SCORE_THRESHOLD;
                    const scorePercentage = suggestion.score === undefined
                        ? undefined
                        : Math.round(suggestion.score * 10);

                    return (
                        <article
                            key={alert.id}
                            className={`${styles.alertCard} ${isLow ? styles.lowAlert : styles.highAlert}`}
                        >
                            <div className={styles.alertDot} aria-hidden="true" />
                            <div className={styles.alertBody}>
                                <div className={styles.alertTitle}>
                                    <strong className={VALUE_CLASS[suggestion.value]}>
                                        {VALUE_LABELS[suggestion.value]}
                                    </strong>
                                    <span>
                                        {isLow
                                            ? " en dessous du seuil recommandé"
                                            : " à valoriser comme point fort"}
                                    </span>
                                </div>

                                <p className={styles.alertMeta}>
                                    {alert.organizationName}
                                    {scorePercentage !== undefined && (
                                        <>
                                            {" · Score actuel : "}
                                            <strong>{scorePercentage}/100</strong>
                                            {" · Seuil : "}
                                            {Math.round(threshold * 10)}
                                        </>
                                    )}
                                </p>

                                {suggestion.actionLabel && (
                                    <button
                                        type="button"
                                        className={styles.actionButton}
                                        onClick={() => onAction?.(alert)}
                                    >
                                        → {suggestion.actionLabel}
                                    </button>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>
        )}
    </section>
);

export default DecisionAlertsPanel;
